import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { usePostFormStore } from '#modules/finder/features/posts/model/postFormStore';
import { useSettingsSections } from '#modules/user/features/profile/components/Settings/useSettingsSections';
import { getProfileQueryOptions } from '#modules/user/features/profile/model/profile';
import { usePostNotificationNavigation } from '#features/notifications/hooks/usePostNotificationNavigation';
import { useSSR } from '#hooks/useSSR';
import { requestGuardedNavigation } from '../model/guardedNavigation';
import { canReturnToOrigin, mobileScreen } from '../model/mobileNavigation';

export const useMobileHeader = (isAuth: boolean) => {
  const { t } = useLingui();
  const { isBrowser } = useSSR();
  const router = useRouter();
  const { origin: notificationOrigin, returnToPost } = usePostNotificationNavigation();
  const { pathname } = useLocation();
  const screen = mobileScreen(pathname);
  const sections = useSettingsSections();
  const step = usePostFormStore((state) => state.step);
  const entries = useRef(new Map<number, string>());
  const client = useQueryClient();
  const cache = client.getQueryCache();
  // Observe existing page data; the header must never introduce another request.
  const entityTitle = useSyncExternalStore(
    (notify) => cache.subscribe(notify),
    () => {
      if (screen.kind !== 'detail' || !screen.id) return '';
      if (screen.entity === 'player') {
        const data = client.getQueryData<
          Awaited<ReturnType<ReturnType<typeof getProfileQueryOptions>['queryFn']>>
        >(getProfileQueryOptions({ displayNameOrId: screen.id }).queryKey);
        return data?.displayName ?? '';
      }
      return '';
    },
    () => ''
  );

  useEffect(() => {
    const remember = () => {
      const location = router.history.location;
      entries.current.set(location.state.__TSR_index, location.href);
      if (entries.current.size > 40) entries.current.delete(entries.current.keys().next().value!);
    };
    remember();
    const unsubscribe = router.history.subscribe(remember);
    const reset = () => entries.current.clear();
    window.addEventListener('teamplay:ios-navigation-identity-reset', reset);
    return () => {
      unsubscribe();
      window.removeEventListener('teamplay:ios-navigation-identity-reset', reset);
    };
  }, [router]);

  const leave = () => {
    if (screen.kind === 'settings') {
      if (screen.section === 'notifications' && notificationOrigin) {
        returnToPost();
        return;
      }
      void router.navigate({ to: '/{-$locale}/dashboard/settings' });
      return;
    }
    const previous = entries.current.get(router.history.location.state.__TSR_index - 1);
    if (canReturnToOrigin(previous, router.history.location.href)) router.history.back();
    else if (screen.kind === 'task' && screen.id)
      void router.navigate({
        to: '/{-$locale}/posts/$postId',
        params: { postId: screen.id },
        replace: true,
      });
    else
      void router.navigate({
        to: isAuth ? '/{-$locale}/dashboard/discover' : '/{-$locale}',
        replace: true,
      });
  };
  const cancel = () => requestGuardedNavigation(leave);
  const back = () => {
    if (screen.kind === 'task' && step > 0) {
      requestGuardedNavigation(() => usePostFormStore.getState().setStep(0), document.body, false);
    } else cancel();
  };
  let title = '';
  if (screen.kind === 'root') {
    title = {
      discover: t`Discover`,
      chats: t`Chats`,
      friends: t({ id: 'friends:title', message: 'Friends' }),
      settings: t`dashboard:settings.title`,
    }[screen.root];
  } else if (screen.kind === 'settings')
    title =
      sections.find(({ slug }) => slug === screen.section)?.name ?? t`dashboard:settings.title`;
  else if (screen.kind === 'task') title = screen.editing ? t`Edit post` : t`post:postCreation`;
  else if (screen.kind === 'detail')
    title =
      entityTitle ||
      (screen.entity === 'post'
        ? t`Post`
        : screen.entity === 'notifications'
          ? t`notifications.title`
          : screen.entity === 'me'
            ? t`My profile`
            : t`Player profile`);

  return {
    screen,
    title,
    back,
    cancel,
    previousStep: screen.kind === 'task' && step > 0,
    ...(isBrowser &&
    screen.kind === 'settings' &&
    screen.section === 'notifications' &&
    notificationOrigin
      ? { backToPost: true }
      : {}),
  };
};
